import React, { useState } from 'react'

import Button from '@mui/material/Button'

import { Alert, CircularProgress, Snackbar, styled, Tooltip } from '@mui/material'
import { useMultipleLocalizedText } from '../context/Localization/localizationHooks'
import useSaveDraft from '../hooks/useSaveDraft'
import { SaveAsDraft } from './SaveAsDraft'

import Dialog from '../pixel/Dialog';
import { useAppDispatch, useAppSelector } from '../hooks/reduxHooks';
import useValidateCriteriaForm from '../hooks/useValidateCriteriaForm'
import { Error } from '@mui/icons-material'
import { updateValidated, validateFields } from '../store/groupConditionsSlice'

const RootContainer = styled('div')(() => ({
    display: 'flex',
    justifyContent: 'space-between',
    backgroundColor: 'white',
    padding: '24px 8px',
}))

const ButtonRoot = styled(Button)(() => ({
    fontSize: '14px',
    textTransform: 'capitalize',
    borderRadius: '2px',
}))

const ButtonTextInfo = styled(ButtonRoot)(() => ({
    color: '#5B6971'
}))

const ButtonTextPrimary = styled(ButtonRoot)(({ error }: { error?: 0 | 1 | undefined }) => ({
    color: error ? 'red' : '#1872CC',
}))

const ButtonOutlinedPrimary = styled(ButtonRoot)(() => ({
    color: '#1872CC',
    borderColor: '#1872CC',
    marginLeft: '8px',
    marginRight: '8px',
}))

const ButtonContainedPrimary = styled(ButtonRoot)(() => ({
    backgroundColor: '#1872CC',
    marginLeft: '8px',
    marginRight: '8px',
}))

export interface FooterNavigationProps {
    fieldsLoading: boolean
    offerService: string
    offerFormEId: string
    onCancel: () => void
    onSkipStep: () => void
    onBack: () => void
    onNext: (groupConditions: any, skipped: boolean, criteriaStatementString: string) => void
}

export const FooterNavigation = ({
    fieldsLoading,
    offerService,
    offerFormEId,
    onBack,
    onCancel,
    onNext,
    onSkipStep,
}: FooterNavigationProps) => {
    const [cancelOpen, setCancelOpen] = useState(false)
    const [serverValidationError, setServerValidationError] = useState('')
    const [draftSnackbar, setDraftSnackbar] = useState(false)
    const [draftFailSnackbar, setDraftFailSnackbar] = useState(false)

    const {
        loading: validateLoading,
        error: validateError,
        validateForm,
    } = useValidateCriteriaForm(offerService)

    const {
        CANCEL,
        BACK,
        NEXT,
        ADMINERRORSAVEASDRAFT,
        ADMINCRITERIAINVALIDFORM,
        ADMINCRITERIASAVEASDRAFTSUCCESS,
        CONFIRM,
        CANCELOFFERFORM,
        UNSAVEDCHANGES,
        ADMINCRTIERIASKIPTHISSTEP,
    } = useMultipleLocalizedText()

    const dispatch = useAppDispatch()
    const groupConditions = useAppSelector(({ groupConditions }) => groupConditions.groupConditions)

    const {
        error,
        loading: draftLoading,
        saveAsDraft,
    } = useSaveDraft(groupConditions, offerService, ADMINERRORSAVEASDRAFT)

    const handleResolve = () => {
        setCancelOpen(false)
        onCancel()
    }

    const handleCancel = () => {
        setCancelOpen(false)
    }

    const openCancelDialog = () => {
        setCancelOpen(true)
    }

    const handleSaveAsDraft = async () => {
        setDraftSnackbar(false)
        setDraftFailSnackbar(false)

        const success = await saveAsDraft(groupConditions)
        if (success)
            setDraftSnackbar(true)
        else {
            setDraftFailSnackbar(true)
        }
    }

    const handleNext = async () => {
        setServerValidationError('')

        if (groupConditions.length === 0) {
            onNext(groupConditions, true, '')
            return
        }

        const localValidation = validateFields(groupConditions)

        if (Object.keys(localValidation).length > 0) {
            dispatch(updateValidated({ validated: true }))
            return
        }

        const { isValidCriteria, criteria, isAlive } = await validateForm(offerFormEId, groupConditions)

        if (isAlive) {
            if (isValidCriteria) {
                onNext(groupConditions, false, criteria)
            } else {
                setServerValidationError(ADMINCRITERIAINVALIDFORM)
            }
        }
    }

    const isLoading = draftLoading || validateLoading

    return (
        <RootContainer>
            <Snackbar open={draftSnackbar} autoHideDuration={6000} onClose={() => setDraftSnackbar(false)}>
                <Alert onClose={() => setDraftSnackbar(false)} severity="success" sx={{ width: '100%' }}>
                    {ADMINCRITERIASAVEASDRAFTSUCCESS}
                </Alert>
            </Snackbar>
            <Snackbar open={draftFailSnackbar} autoHideDuration={6000} onClose={() => setDraftFailSnackbar(false)}>
                <Alert onClose={() => setDraftFailSnackbar(false)} severity="error" sx={{ width: '100%' }}>
                    {ADMINERRORSAVEASDRAFT}
                </Alert>
            </Snackbar>
            <Dialog
                open={cancelOpen}
                resolveActions={[{ label: CONFIRM }]}
                rejectActions={[{ label: CANCEL }]}
                title={CANCELOFFERFORM}
                description={UNSAVEDCHANGES}
                onResolve={handleResolve}
                onReject={handleCancel}
                onClose={() => { }}
                onExited={() => { }}
            />
            <div style={{ display: 'flex', alignItems: 'center' }}>
                <ButtonTextInfo
                    variant="text"
                    color="info"
                    onClick={openCancelDialog}
                    disabled={isLoading}
                >
                    {CANCEL}
                </ButtonTextInfo>
                <SaveAsDraft
                    loading={draftLoading}
                    error={error}
                    hasDifference={true}
                    handleSaveAsDraft={handleSaveAsDraft}
                    disabled={isLoading || fieldsLoading}
                />
            </div>
            <div style={{ display: 'flex', flexGrow: 1, justifyContent: 'end', alignItems: 'center' }}>
                {/*
                <ButtonTextPrimary 
                    variant="text" 
                    color='primary' 
                    onClick={onSkipStep}
                    disabled={isLoading}
                >
                    {ADMINCRTIERIASKIPTHISSTEP}
                </ButtonTextPrimary> 
                */}
                <ButtonOutlinedPrimary
                    variant="outlined"
                    color='primary'
                    onClick={onBack}
                    disabled={isLoading}
                >
                    {BACK}
                </ButtonOutlinedPrimary>
                <ButtonContainedPrimary
                    variant="contained"
                    color='primary'
                    onClick={handleNext}
                    disabled={isLoading || fieldsLoading}
                >
                    { validateLoading && !draftLoading ? <CircularProgress size={17} /> : NEXT }
                </ButtonContainedPrimary>
                {(validateError || serverValidationError) && (
                    <Tooltip title={validateError || serverValidationError} placement="top" arrow>
                        <Error htmlColor="red" />
                    </Tooltip>
                )}
            </div>
        </RootContainer>
    )
}

export default FooterNavigation